import React, {useState, useRef, useEffect} from 'react';
import {ConditionalTooltip} from '../../../../components/tooltip';

/**
 * Represents a single toggle option
 * @interface ToggleOption
 * @property {string} value - Unique identifier for the option
 * @property {string} label - Display text for the option
 * @property {React.ReactNode} content - Content to display when this option is selected
 * @property {React.ReactNode} [icon] - Optional icon to display alongside or instead of label
 * @property {boolean} [isDisabled] - Whether this specific option is disabled
 * @property {string} [disabledTooltipText] - Tooltip text to show when option is disabled
 */
interface ToggleOption {
    value: string;
    label: string;
    content: React.ReactNode;
    icon?: React.ReactNode;
    isDisabled?: boolean;
    disabledTooltipText?: string;
}

/**
 * Props for the MultiWayToggle component
 * @interface MultiWayToggleProps
 * @property {ToggleOption[]} options - Array of toggle options
 * @property {string} [defaultValue] - Initial selected value
 * @property {(value: string) => void} [onChange] - Callback when selection changes
 * @property {string} [className] - Additional CSS classes
 * @property {'sm' | 'md' | 'lg'} [size] - Size variant of the toggle
 * @property {boolean} [disabled] - Whether the entire toggle is disabled
 * @property {'horizontal' | 'vertical'} [orientation] - Layout orientation
 * @property {boolean} [showContent] - Whether to display the content below the toggle (default: true)
 * @property {'left' | 'right'} [alignment] - Controls toggle alignment ('left' | 'right')
 */
interface MultiWayToggleProps {
    options: ToggleOption[];
    defaultValue?: string;
    onChange?: (value: string) => void;
    className?: string;
    size?: 'sm' | 'md' | 'lg';
    disabled?: boolean;
    orientation?: 'horizontal' | 'vertical';
    showContent?: boolean;
    alignment?: 'left' | 'right';
}

// Size classes for different toggle sizes
const sizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
};

// Orientation classes
const orientationClasses = {
    horizontal: 'flex-row',
    vertical: 'flex-col',
};

const MultiWayToggle: React.FC<MultiWayToggleProps> = ({
    options,
    defaultValue,
    onChange,
    className = '',
    size = 'md',
    disabled = false,
    orientation = 'horizontal',
    showContent = true,
    alignment = 'left',
}) => {
    // State to track currently selected value
    const [selectedValue, setSelectedValue] = useState<string>(defaultValue || options[0].value);
    const [sliderStyle, setSliderStyle] = useState<React.CSSProperties>({});
    
    // Refs for measuring actual button dimensions
    const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    /**
     * Handles toggle selection change
     * @param {string} value - The new selected value
     * @param {boolean} isOptionDisabled - Whether the specific option is disabled
     */
    const handleToggle = (value: string, isOptionDisabled?: boolean) => {
        if (disabled || isOptionDisabled) return;

        setSelectedValue(value);
        onChange?.(value);
    };

    /**
     * Calculates slider position based on actual button dimensions
     */
    const updateSliderPosition = () => {
        const selectedIndex = options.findIndex((option) => option.value === selectedValue);
        const selectedButton = buttonsRef.current[selectedIndex];
        const container = containerRef.current;

        if (!selectedButton || !container) return;

        const containerRect = container.getBoundingClientRect();
        const buttonRect = selectedButton.getBoundingClientRect();

        if (orientation === 'vertical') {
            const relativeTop = buttonRect.top - containerRect.top;
            setSliderStyle({
                height: `${buttonRect.height}px`,
                top: `${relativeTop}px`,
                left: '0',
                right: '0',
                transition: 'top 0.2s ease-in-out, height 0.2s ease-in-out',
            });
        } else {
            const relativeLeft = buttonRect.left - containerRect.left;
            setSliderStyle({
                width: `${buttonRect.width}px`,
                left: `${relativeLeft}px`,
                top: '0',
                bottom: '0',
                transition: 'left 0.2s ease-in-out, width 0.2s ease-in-out',
            });
        }
    };

    // Update slider position when selection changes or component mounts
    useEffect(() => {
        // Use setTimeout to ensure DOM is fully rendered
        const timer = setTimeout(() => {
            updateSliderPosition();
        }, 10);

        return () => clearTimeout(timer);
    }, [selectedValue, orientation, options]);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            updateSliderPosition();
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [selectedValue, orientation]);

    // Find the currently selected option to display its content
    const selectedOption = options.find((option) => option.value === selectedValue);

    return (
        <div className={`multi-way-toggle-component w-full ${className}`}>
            {/* Toggle Controls */}
            <div
                className={`w-full ${
                    alignment === 'right' ? 'flex justify-end' : 'flex justify-start'
                }`}
            >
                <div className='inline-flex'>
                    <div
                        ref={containerRef}
                        className={`
                            relative bg-gray-200 rounded-lg p-1 
                            ${sizeClasses[size]} 
                            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            ${orientation === 'vertical' ? 'flex-col' : 'flex-row'}
                        `}
                    >
                        {/* Sliding background indicator */}
                        <div
                            className='absolute bg-white rounded-md shadow-sm'
                            style={sliderStyle}
                        />

                        {/* Toggle options container */}
                        <div className={`relative flex ${orientationClasses[orientation]}`}>
                            {options.map((option: any, index: number) => (
                                <ConditionalTooltip
                                    content={option.disabledTooltipText}
                                    key={option.value}
                                    alignment={'bottom'}
                                    condition={!!option.isDisabled}
                                >
                                    <button
                                        ref={(el) => (buttonsRef.current[index] = el)}
                                        onClick={() =>
                                            handleToggle(option.value, option.isDisabled)
                                        }
                                        disabled={disabled || option.isDisabled}
                                        className={`
                                            relative z-10 flex-1 px-4 py-1 rounded-md transition-colors duration-200 font-medium whitespace-nowrap
                                            flex items-center justify-center gap-2 text-center
                                            ${selectedValue === option.value ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'}
                                            ${disabled || option.isDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                                            ${orientation === 'vertical' ? 'w-full' : ''}
                                        `}
                                        type='button'
                                        aria-pressed={selectedValue === option.value}
                                        aria-label={`Select ${option.label}`}
                                        {...(option['data-testid'] ? {'data-testid': option['data-testid']} : {})}
                                    >
                                        {/* Render icon if provided */}
                                        {option.icon && (
                                            <span className='flex-shrink-0'>{option.icon}</span>
                                        )}

                                        {/* Render label */}
                                        <span className='text-center'>{option.label}</span>
                                    </button>
                                </ConditionalTooltip>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Content Display */}
            {showContent && selectedOption && (
                <div className='toggle-content w-full'>{selectedOption.content}</div>
            )}
        </div>
    );
};

export default MultiWayToggle;
export {type MultiWayToggleProps, type ToggleOption};